/* ═══════════════════════════════════════════════════════════════
   View mode nudges toward Edit (SPEC_WORKFLOW §4; Feel-test R2 item 4).

   View shows the published text, read-only. Every control that only works in
   Edit stays visible, looks muted, stays focusable (aria-disabled — never
   native `disabled`), explains itself on hover AND focus, and when pressed
   gives the Edit button one quiet pulse. No toast, no modal, no auto-enter.
   People without edit rights get the explanation and no pulse.

   ONE shared behaviour: Studio provides the context; controls use
   <ViewModeGuard> (one focusable element), useViewModeGuard() read directly
   by kit-owned buttons (toolbar clusters), or guardHandler() for callbacks (menu items, tag clicks, compare actions).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Hint } from '../components/ui/tooltip';

export interface ViewModeValue {
  /** Edit mode is on (and the user may edit). */
  editing: boolean;
  /** The user holds an edit privilege — Edit is theirs to press. */
  canEdit: boolean;
  /** Pulse the Edit button (no-op without edit rights). */
  nudgeEdit: () => void;
  /** Why controls are inert, when it is not View mode (e.g. the publish
   *  walk). Defaults to the View-mode reason. */
  reason?: string;
}

/** Default = editing, so anything rendered outside the Studio is never guarded. */
export const ViewModeContext = React.createContext<ViewModeValue>({
  editing: true,
  canEdit: true,
  nudgeEdit: () => undefined,
});

export function viewModeReason(canEdit: boolean): string {
  return canEdit ? 'Click Edit to make changes' : 'You have view access only';
}

export function useViewModeGuard() {
  const vm = React.useContext(ViewModeContext);
  return {
    inert: !vm.editing,
    reason: vm.reason ?? viewModeReason(vm.canEdit),
    nudgeEdit: vm.nudgeEdit,
    guard: <A extends unknown[], R>(fn: (...a: A) => R) => guardHandler(vm, fn),
  };
}

/** A callback that only runs in Edit; in View it nudges instead. */
export function guardHandler<A extends unknown[], R>(
  vm: ViewModeValue,
  fn: (...a: A) => R
): (...a: A) => R | undefined {
  return (...a: A) => {
    if (vm.editing) return fn(...a);
    vm.nudgeEdit();
    return undefined;
  };
}

interface GuardableProps {
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
}

/** Wrap ONE focusable element. Edit mode: untouched. View mode: muted,
 *  aria-disabled, swallows the press, explains itself, nudges Edit. */
export function ViewModeGuard({ children }: { children: React.ReactElement<GuardableProps> }) {
  const { inert, reason, nudgeEdit } = useViewModeGuard();
  if (!inert) return children;
  const child = children;
  const swallow = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    nudgeEdit();
  };
  return (
    <Hint text={reason}>
      {React.cloneElement(child, {
        'aria-disabled': true,
        className: [child.props.className, 'sps-vm-inert'].filter(Boolean).join(' '),
        onClick: (e: React.MouseEvent<HTMLElement>) => swallow(e),
        onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key === 'Enter' || e.key === ' ') swallow(e);
          else child.props.onKeyDown?.(e);
        },
      } as Partial<GuardableProps> & { 'aria-disabled': boolean })}
    </Hint>
  );
}
